/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { SxProps } from "@mui/material";
import { IFilterSelectBox } from "../../Shared/Filter/types";
import { useSelector } from "react-redux";
//import { DEVICEGROUPS, SEVERITYLIST } from "../../../globals/globals";
import { useStoreDispatch } from "../../../store/hooks";
import {
  setSelectedDeviceStatus,
  setSelectedDeviceType,
  setSelectedDeviceConfigStatus,
  setSelectedDeviceVersion,
  setSelectedDeviceKpiStatus,
  setSelectedLabels,
  setRegionFilterIds
} from "../../../store/filters/Nodes/reducer";
import { devicesSelector, urlParameterSelector } from "../../../store/filters/Nodes/selectors";
import { useEffect, useMemo } from "react";
import { useGetSwVersionsQuery, useGetLabelsQuery } from "../../../store/api/devices/deviceApiSlice";
import { DEVICESTATUS } from "../../../globals/globals";
import { useRegionDisplay } from "../../../pages/Administrations/AccessControls/Regions/hooks";
import { selectedGeoRegionIdsSelector } from "../../../store/api/admin/selectors";
import { IGeoRegion } from "../../../store/api/admin/types";

export const useVersionsDisplay = () => {
  const filters = useSelector(urlParameterSelector);
  const args = { filters: filters.toString() };

  const { data = [], isLoading, refetch } = useGetSwVersionsQuery(args);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return {
    data,
    isLoading,
  };
};

export const useLabelsDisplay = () => {

  // Call the new API (replace with your actual API slice)
  const { data = [], isLoading, refetch } = useGetLabelsQuery();

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Convert API to array of strings → ["Label1", "Label2"]
  const labelNames: string[] = data.map((item: any) => item.labelName);

  return {
    data: labelNames,
    isLoading,
  };
};

export const useNodesFilter = () => {
  const deviceFilterData = useSelector(devicesSelector);
  const dispatch = useStoreDispatch();
  const versionList = useVersionsDisplay();
  const deviceLabelList = useLabelsDisplay();
  const { geoRegions } = useRegionDisplay();
  const selectedGeoRegionIds = useSelector(selectedGeoRegionIdsSelector);
  //const deviceGroups = Object.values(DEVICEGROUPS);
  //const filterGroups = deviceGroups.filter((group) => group.Models.length).map((filter) => filter.Text);

  const regionOptions = useMemo(() => {
    if (!geoRegions || geoRegions.length === 0) return [];

    const baseRegions = selectedGeoRegionIds && selectedGeoRegionIds.length
      ? geoRegions.filter((region: IGeoRegion) => selectedGeoRegionIds.includes(region.objectKey))
      : geoRegions;

    return baseRegions
      .map((region: IGeoRegion) => ({ value: region.objectKey, text: region.name }))
      .sort((a, b) => (a.text || "").localeCompare(b.text || ""));
  }, [geoRegions, selectedGeoRegionIds]);

  // Synchronize local region filter with global region filter changes
  useEffect(() => {
    if (!selectedGeoRegionIds || selectedGeoRegionIds.length === 0) return;
    if (!deviceFilterData.regionFilterIds || deviceFilterData.regionFilterIds.length === 0) return;

    // Filter out any regions that are no longer in the global selection
    const validRegions = deviceFilterData.regionFilterIds.filter((regionId: string) =>
      selectedGeoRegionIds.includes(regionId)
    );

    // If some regions were filtered out, update the local filter
    if (validRegions.length !== deviceFilterData.regionFilterIds.length) {
      dispatch(setRegionFilterIds(validRegions));
    }
  }, [selectedGeoRegionIds, deviceFilterData.regionFilterIds, dispatch]);

  const filters: IFilterSelectBox[] = [
    // {
    //   id: 'site', label: 'SITE',
    //   value: deviceFilterData.selectedDeviceSite && deviceFilterData.deviceHostNames.includes(deviceFilterData.selectedDeviceSite)
    //     ? deviceFilterData.selectedDeviceSite : '',
    //   setValue: setSelectedDeviceSite,
    //   values: [...deviceFilterData.deviceHostNames]
    // },
    {
      id: "regionFilter",
      label: "REGION",
      value: deviceFilterData.regionFilterIds || [],
      setValue: setRegionFilterIds,
      values: regionOptions,
      width: "140px",
      multiple: true,
    },
    {
      id: "status",
      label: "STATUS",
      value: deviceFilterData.selectedDeviceStatus || "",
      setValue: setSelectedDeviceStatus,
      values: [
        { value: DEVICESTATUS.NORMAL, text: "Connected" },
        { value: DEVICESTATUS.TIMEOUT, text: "Disconnected" },
      ],
      width: "120px",
    },
    {
      id: "configstatus",
      label: "CONFIG STATUS",
      value: deviceFilterData.selectedDeviceConfigStatus || "",
      setValue: setSelectedDeviceConfigStatus,
      values: ["NA", "Synchronized", "Pending", "Commit_Error", "Resynchronizing", "Synchronization_Failed", "Offline", "Mismatch"],
      width: "180px",
    },
    {
      id: "type",
      label: "TYPE",
      value: deviceFilterData.selectedDeviceType || "",
      setValue: setSelectedDeviceType,
      values: [
        { value: "SCSN-9%", text: "SN" },
        { value: "CU-%", text: "CU" },
        { value: "SCSN-10000%", text: "RCP" },
        { value: "ME1200%", text: "ME1200" },
        { value: "MA6000-G2%", text: "MA6000-G2" },
        { value: "MA6200-G2%", text: "MA6200-G2" },
        { value: "MA6200-G1%", text: "MA6200-G1" },
        { value: "MA3000%", text: "MA3000" },
      ],
      width: "100px",
      preserveOptionCase: true,
    },
    {
      id: "version",
      label: "VERSION",
      value: deviceFilterData.selectedDeviceVersion || "",
      setValue: setSelectedDeviceVersion,
      values: versionList?.data || [], // version list will add from server
      width: "100px",
    },
    {
      id: "kpistatus",
      label: "KPI SYNC",
      value: deviceFilterData.pmCollected || "",
      setValue: setSelectedDeviceKpiStatus,
      values: [
        { value: "false", text: "Error" },
        { value: "true", text: "Success" },
      ],
      width: "130px",
    },
    {
      id: "deviceLabel",
      label: "LABELS",
      value: deviceFilterData.selectedLabels || [],
      setValue: setSelectedLabels,
      values: (deviceLabelList?.data?.sort() || ""),
      width: "110px",
      multiple: true,
    },
  ];

  const formControlSx: SxProps = { minWidth: 100, marginRight: "10px" };
  const searchTooltip = "Search by 'Host Name', 'Version', 'IP Address' or 'Cluster ID' or 'Label Name' fields for quick access.";
  return {
    dispatch,
    filters,
    formControlSx,
    searchTooltip,
  };
};
